import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { PlanId } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'https://doopl-api.joyburhaan.workers.dev';

export interface User {
  id: string;
  email: string;
  name: string | null;
  planId: PlanId;
  createdAt: string;
  postsThisMonth?: number; // Count of posts created this month
}

export interface UserSettings {
  default_platforms: string; // JSON array
  link_expiry_days: number;
  brand_name: string | null;
}

interface AuthContextType {
  user: User | null;
  settings: UserSettings | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isNewUser: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  setIsNewUser: (value: boolean) => void;
  clearNewUserFlag: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'doopl_token';
const USER_KEY = 'doopl_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(TOKEN_KEY);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  // Persist token and user to localStorage
  const saveAuth = useCallback((newToken: string | null, newUser: User | null) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    if (newUser) {
      localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setToken(newToken);
    setUser(newUser);
  }, []);

  // Verify token and load user on mount
  useEffect(() => {
    async function verifyAuth() {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          setSettings(data.settings);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        } else {
          // Token invalid or expired
          saveAuth(null, null);
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        // Keep existing user data if offline
      } finally {
        setIsLoading(false);
      }
    }

    verifyAuth();
  }, [token, saveAuth]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Login failed' };
      }

      saveAuth(data.token, data.user);
      setSettings(data.settings || null);
      setIsNewUser(data.isNewUser || false);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [saveAuth]);

  const signup = useCallback(async (email: string, password: string, name?: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Signup failed' };
      }

      saveAuth(data.token, data.user);
      setIsNewUser(true);

      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [saveAuth]);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      saveAuth(null, null);
      setSettings(null);
      setIsNewUser(false);
    }
  }, [token, saveAuth]);

  const refreshToken = useCallback(async () => {
    if (!token) return false;

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        localStorage.setItem(TOKEN_KEY, data.token);
        return true;
      }

      // Refresh failed, log out
      saveAuth(null, null);
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [token, saveAuth]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Refresh user data from server (e.g., after Stripe checkout)
  const refreshUser = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setSettings(data.settings);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, [token]);

  const clearNewUserFlag = useCallback(() => {
    setIsNewUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        settings,
        token,
        isLoading,
        isAuthenticated: !!user && !!token,
        isNewUser,
        login,
        signup,
        logout,
        refreshToken,
        refreshUser,
        updateUser,
        setIsNewUser,
        clearNewUserFlag,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for making authenticated API calls
 * Automatically adds Authorization header and handles 401 responses
 * Uses a ref to always get the current token, avoiding stale closure issues
 */
export function useAuthenticatedFetch() {
  const { token, refreshToken, logout } = useAuth();

  // Use a ref to always have access to the current token
  // This prevents stale token issues when the token updates mid-request
  const tokenRef = useRef(token);
  tokenRef.current = token;

  return useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${tokenRef.current}`,
        },
      });

      // If unauthorized, try to refresh token
      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          // Retry with the refreshed token (now available via ref)
          return fetch(fullUrl, {
            ...options,
            headers: {
              ...options.headers,
              Authorization: `Bearer ${tokenRef.current}`,
            },
          });
        } else {
          // Refresh failed, log out
          await logout();
        }
      }

      return response;
    },
    [refreshToken, logout]
  );
}
